import { Container, Divider, Grid } from '@material-ui/core';
import { createTheme } from '@material-ui/core/styles';
import { Backdrop, CircularProgress } from '@mui/material';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import ptBR from 'date-fns/esm/locale/pt-BR';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import SelectDate from '../../../components/Form/SelectDate';
import Success from '../../../components/Form/Success';
import TextField from '../../../components/Form/TextField';
import ButtonUI from '../../../components/ui/ButtonUI';
import PageTitle from '../../../components/ui/PageTitleUI';
import { useAuth } from '../../../hooks/auth';
import { DebitConferencePost } from '../../../interfaces/Conference';
import ConferenceApi from '../../../services/Conference';
import permissions from '../../../utils/Permissions';
import { useStyles } from './Style';

interface Params {
    Id: string;
}

const opcoes = [
    { url: '/DebitInsert', descricao: 'Incluir', state: {}},
    { url: '/DebitSearch', descricao: 'Pesquisar', state: {}}
]

const DebitInsert: React.FC = () => {
    const location = useLocation<Params>()
    const classes = useStyles();
    const [id, setId] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const { user, hasPermission } = useAuth();
    const { t: _t } = useTranslation("General");
    const { t: t } = useTranslation("DebitInsert");
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const [success, setSuccess] = useState(false);
    
    useEffect(() => {
        if (!hasPermission(permissions.DEBIT.Search))
            history.push('/NoPermission')
    }, [history, user]);

    //Campos do Form
    interface IDataRef {
        label: string;
        code: string;
        month: string;
        startMonth: Date;
        endMonth: Date;
    }

    interface IForm {
        dataRef: IDataRef;
        ApportionmentValue: string;
        LimitationValue: string;
        ApproverUserName1: string;
        ApproverUserName2: string;
        ApproverUserName3: string;
        IdCompany: string;
        IdPlant: string;
        DataInicial: string;
        DataFinal: string;
    }

    //Metodos do form:
    const { handleSubmit, reset, control, getValues, setValue, clearErrors, setError } = useForm<IForm>({
        defaultValues: {
            dataRef: {},
            ApportionmentValue: '',
            LimitationValue: '',
            ApproverUserName1: '',
            ApproverUserName2: '',
            ApproverUserName3: '',
            IdCompany: user.company.companyID,
            IdPlant: user.company.plantID,
        }
    });

    useEffect(() => {
        if (location.state?.Id)
            setId(location.state.Id);

    }, [location.state?.Id]);

    
    useEffect(() => {
        setLoading(true);
        if (id)
        {
            
            ConferenceApi.get(id).then(result => {
                const datRef = new Date(result.referenceDate);
                setValue('ApportionmentValue', result.apportionmentValue);
                setValue('LimitationValue', result.limitationValue);
                setValue('ApproverUserName1', result.approverUserName1);
                setValue('ApproverUserName2', result.approverUserName1);
                setValue('ApproverUserName3', result.approverUserName1);
                setValue('dataRef', {
                    label: format(datRef, 'LLLL', { locale: ptBR }) + '/' + datRef.getUTCFullYear(),
                    code: format(datRef, 'LLLL', { locale: ptBR }) + '/' + datRef.getUTCFullYear(),
                    month: format(datRef, 'MM'),
                    startMonth: startOfMonth(datRef),
                    endMonth: endOfMonth(datRef),
                });
                setValue('DataInicial', format(startOfMonth(datRef), 'yyyy-MM-dd'));
                setValue('DataFinal', format(endOfMonth(datRef), 'yyyy-MM-dd'));

            }).catch((err) => {
                enqueueSnackbar(err, { variant: 'warning' })
            }).finally(() => {
                setLoading(false);
            });
        }else{
            setLoading(false);
        }
    }, [id]);

    const onSubmit = async (data: IForm) => {

        const erros = await yupValidation(data)

        if (!erros)
            return
        
        setLoading(true);
        
        let post_: DebitConferencePost = {
            id: id,
            ApproverUserName1: data.ApproverUserName1,
            ApproverUserName2: data.ApproverUserName2,
            ApproverUserName3: data.ApproverUserName3,
            LimitationValue: data.LimitationValue,
            ApportionmentValue: data.ApportionmentValue,
            IdCompany: user.company.companyID,
            IdPlant: user.company.plantID,
            ReferenceDate:  data.dataRef &&
                            data.dataRef.startMonth ? 
                            format(data.dataRef.startMonth, "yyyy-MM-dd'T'HH:mm:ss") : '',
        }

        if (id)
        {
            ConferenceApi.update(post_)
            .then(result => {
                enqueueSnackbar(_t("returnSubmitEditFormSuccess"), { variant: 'success' }); 
                setSuccess(true);
            }).catch((err) => {
                // enqueueSnackbar(err.message, { variant: 'warning' })
            }).finally(() => {
                setLoading(false);
            });
        }else{
            ConferenceApi.create(post_)
            .then(result => {
                enqueueSnackbar(_t("returnSubmitEditFormSuccess"), { variant: 'success' });
                setSuccess(true);
            }).catch((err) => {
                // enqueueSnackbar(err.Message, { variant: 'warning' })
            }).finally(() => {
                setLoading(false);
                // history.push('/DebitSearch');
            });
        }
        
    }

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: ''                    
                }
            },
        }
    })

    const yupValidation = async (data: any) => {
        clearErrors();
        try {
            const schema = Yup.object().shape({
                dataRef: Yup.object().nullable()
                    .required("requiredFieldValidation")
                    .shape({startMonth: Yup.string().required(_t("requiredFieldValidation"))}),
                ApportionmentValue: Yup.number()
                    .required("requiredFieldValidation")
                    .typeError("requiredFieldValidation"),
                LimitationValue: Yup.number()
                    .required("requiredFieldValidation")
                    .typeError("requiredFieldValidation"),
                    ApproverUserName1: Yup.string().required(_t("requiredFieldValidation")),
                    ApproverUserName2: Yup.string().required(_t("requiredFieldValidation")),
                    ApproverUserName3: Yup.string().required(_t("requiredFieldValidation")),
            })

            await schema.validate(data, {
                abortEarly: false,
            })

        } catch (err) {
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach(error => {
                    setError(error.path?.split('.')[0] as "dataRef" | "ApportionmentValue" | "LimitationValue" | "ApproverUserName1" | "ApproverUserName2" | "ApproverUserName3", 
                    { message: error.message })
                })
            }
            return false;
        }

        return true;
    }

   

    if (success)
        return (
            <Container maxWidth={false} component="main">
                <Success opcoes={opcoes}/>
            </Container>
        )
    else
        return (
            <Container maxWidth={false} component="main">
                <Backdrop style={{ zIndex: 9999 }} open={loading}>
                    <CircularProgress color="inherit" />
                </Backdrop>

                <PageTitle title={t("debitInsertTitle")} />
                <div className={classes.rootContent}>
                    <div className={classes.paper}>

                        <form translate={undefined} onSubmit={handleSubmit(onSubmit)}  >
                            <SelectDate
                                label={t("referenceDate")} 
                                name="dataRef" 
                                enablePeriod={false}
                                disabled={id ? true : false}
                                control={control} 
                                setValue={setValue}
                                style={{width: '100%'}}
                                dateAndTime
                            />

                            <Grid container spacing={2}>
                                <Grid item xs={2}>
                                    <TextField type="number" className={classes.inputCurrency} label={t("apportionment")} name="ApportionmentValue" control={control} fullWidth/>
                                </Grid>
                                <Grid item xs={2}>
                                    <TextField type="number" className={classes.inputCurrency} label={t("limitation")} name="LimitationValue" control={control} fullWidth/>
                                </Grid>

                                <Grid item xs={2}>
                                    <TextField label={t("approveUser1")} name="ApproverUserName1" control={control} fullWidth/>
                                </Grid>
                                <Grid item xs={2}>
                                    <TextField label={t("approveUser2")} name="ApproverUserName2" control={control} fullWidth/>
                                </Grid>
                                <Grid item xs={2}>
                                    <TextField label={t("approveUser3")} name="ApproverUserName3" control={control} fullWidth/>
                                </Grid>
                            </Grid>
                            
                            <Divider style={{ margin: '10px' }} />

                            <Grid container spacing={1}>
                                <Grid item xs={12} style={{ padding: "0px" }}>
                                    <ButtonUI onClick={() => history.push('/DebitSearch')}>{_t("cancel")}</ButtonUI>
                                    <ButtonUI  type="submit" color="primary" >Gravar</ButtonUI>
                                </Grid>
                            </Grid>
                        </form>
                    
                    </div>
                </div>
            </Container>
        )
}
export default DebitInsert;
